import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Core2Config} from '../../shared';
import {
  CouponContainerModel,
  CouponLineContainerModel,
  CouponSerialNumberContainerModel
} from '../../../../../../models/aggregates/erp/coupon';
import {ApiResponseModel, ApiVisa, BasicApiResponseModel, Pagination, PagingResponseModel} from '../../shared';
import {Observable} from 'rxjs';
import {Optional} from 'typescript-optional';
import { VoucherSummaryResponseDto } from '../../../data-transfer-objects/alg/cc/voucher-summary-response-dto.model';
import { serialNumberInquiryResponseDto } from '../../../data-transfer-objects/alg/cc';
import {CouponRedemptionRequestDtoModel } from '../../../data-transfer-objects'
import { BaseApiPublicLoginTemplateService } from '../../shared/base-api-public-login-template.service';

@Injectable({providedIn: 'root'})
export class CouponService extends BaseApiPublicLoginTemplateService<CouponContainerModel, CouponContainerModel, CouponContainerModel> {
  constructor(http: HttpClient) {
    super();
    this.apiUrl = Core2Config.DOMAIN_URL + Core2Config.TENANT_URL_PREFIX + Core2Config.DOMAIN_PREFIX + Core2Config.ERP_PREFIX + 'coupons';
    this.endpoint_path = Core2Config.TENANT_URL_PREFIX  + Core2Config.DOMAIN_PREFIX + Core2Config.ERP_PREFIX + 'coupons';
    this.endpoint_path_website_ep_url = Core2Config.TENANT_URL_PREFIX  + Core2Config.DOMAIN_PREFIX + Core2Config.ERP_PREFIX + 'coupons/website-ep'
    this.httpClient = http;
  }

  // ----------------------------Image files related endpoints-----------------------------------------


  static filesToFormData(files: File[], formDataKeyValue: Optional<string>): FormData {
    const formData = new FormData();
    if (!files || files.length === 0) { return formData; }
    files.forEach(file => formData.append(formDataKeyValue.orElse('files'), file));
    return formData;
  }

  private static fileToFormData(file: File, formDataKeyValue: Optional<string>): FormData {
    return CouponService.filesToFormData([file], formDataKeyValue);
  }

  addFilesWithDetails(formData: FormData, subjectGuid, visa: ApiVisa) {
    let url = this.apiUrl + '/add-files/' + subjectGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/add-files/' + subjectGuid;
    }
    return this.httpClient.put(url, formData, this.getHttpHeader(visa));
  }

  replaceFileWithDetails(formData: FormData, imageGuid, visa: ApiVisa) {
    let url = this.apiUrl + '/replace-file/' + imageGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/replace-file/' + imageGuid;
    }
    return this.httpClient.put(url, formData, this.getHttpHeader(visa));
  }

  deleteFile(extensionGuid, visa: ApiVisa) {
    let url = this.apiUrl + '/file/' + extensionGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/file/' + extensionGuid;
    }
    return this.httpClient.delete(url, this.getHttpHeader(visa));
  }

  getFile(extGuid: string, visa: ApiVisa): Observable<Blob> {
    let url = this.apiUrl + '/file/' + extGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/file/' + extGuid;
    }

    return this.httpClient.get<Blob>(url, this.getHttpHeaderBlob(visa));
  }


  getByCriteriaHdr(pagination: Pagination, visa: ApiVisa):
    Observable<PagingResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/headers/'  + 'query?' + pagination.getQueryKeyValues();
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/headers/'  + 'query?' + pagination.getQueryKeyValues();
    }
    return this.httpClient.get<PagingResponseModel<CouponContainerModel>>
    (url, this.getHttpHeader(visa));
  }

  getByGuidHdr(guid: string, visa: ApiVisa): Observable<BasicApiResponseModel<CouponContainerModel>> {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = visa.api_domain_url + this.endpoint_path;
    }
    return this.httpClient.get<BasicApiResponseModel<CouponContainerModel>>(
      this.apiUrl + '/headers/' + guid,
      this.getHttpHeader(visa)
    );
  }

  // ---------------Serial number related endpoints-----------------------

  // call API by query for coupon lines
  getByCriteriaLine(pagination: Pagination, visa: ApiVisa):
    Observable<PagingResponseModel<CouponSerialNumberContainerModel>> {
    let url = this.apiUrl + '/lines/'  + 'query?' + pagination.getQueryKeyValues();
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/lines/'  + 'query?' + pagination.getQueryKeyValues();
    }
    return this.httpClient.get<PagingResponseModel<CouponSerialNumberContainerModel>>
    (url, this.getHttpHeader(visa));
  }

  // call API by guid for coupon lines
  getByGuidLine(guid: string, visa: ApiVisa): Observable<BasicApiResponseModel<CouponSerialNumberContainerModel>> {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = visa.api_domain_url + this.endpoint_path;
    }
    return this.httpClient.get<BasicApiResponseModel<CouponSerialNumberContainerModel>>(
      this.apiUrl + '/lines/' + guid,
      this.getHttpHeader(visa)
    );
  }

  generateSerialNumber(hdrGuid: string, count: string, snLength: string, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/generation/' + hdrGuid + '?count=' + count + '&sn-length=' + snLength;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/generation/' + hdrGuid + '?count=' + count
        + '&sn-length=' + snLength;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null, this.getHttpHeader(visa));
  }

  cancelSerialNumber(lineGuid: string, cancel: boolean, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/cancellation/' + lineGuid + '?cancel=' + cancel;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/cancellation/' + lineGuid + '?cancel=' + cancel;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null,  this.getHttpHeader(visa));
  }

  assignToEntity(lineGuid: string, entityHdrGuid: string, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/assignment/' + lineGuid + '/entity/' + entityHdrGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/assignment/' + lineGuid + '/entity/' + entityHdrGuid;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null,  this.getHttpHeader(visa));
  }

  assignToEntityLoginEntityEp(lineGuid: string, entityHdrGuid: string, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/assignment/login-entity-ep/' + lineGuid + '/entity/' + entityHdrGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/assignment/login-entity-ep/' + lineGuid + '/entity/' + entityHdrGuid;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
      (url, null, this.getHttpHeader(visa));
  }

  // ------------------------Backoffice API-----------------
  getLinesBackOffice(pagination: Pagination, visa: ApiVisa):
    Observable<PagingResponseModel<CouponLineContainerModel>> {
    let url = this.apiUrl + '/lines/backoffice-ep/'  + 'query?' + pagination.getQueryKeyValues();
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/lines/backoffice-ep/'  + 'query?' + pagination.getQueryKeyValues();
    }
    return this.httpClient.get<PagingResponseModel<CouponLineContainerModel>>
    (url, this.getHttpHeader(visa));
  }

  generateSNBackOffice(hdrGuid: string, count: string, snLength: string,  spLength: string, softpin: string, prefix: string, urlKeyGeneration: boolean, urlKeyLength: number, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/generation/backoffice-ep?hdr-guid=' + hdrGuid
      + '&count=' + count + '&sn-length=' + snLength +  '&softpin-generation=' + softpin + '&sp-length=' + spLength + '&item-prefix=' + prefix + '&url-key-generation=' + urlKeyGeneration + '&url-key-length=' + urlKeyLength;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/generation/backoffice-ep?hdr-guid='
        + hdrGuid + '&count=' + count + '&sn-length=' + snLength  +  '&softpin-generation=' + softpin + '&sp-length=' + spLength + '&item-prefix=' + prefix + '&url-key-generation=' + urlKeyGeneration + '&url-key-length=' + urlKeyLength;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null, this.getHttpHeader(visa));
  }

  generateSNLoginEntity(hdrGuid: string, entityHdrGuid: string, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/generation/login-entity-ep?hdr-guid=' + hdrGuid + '&entity-guid=' + entityHdrGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/generation/login-entity-ep?hdr-guid='
        + hdrGuid + '&entity-guid=' + entityHdrGuid;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null, this.getHttpHeader(visa));
  }

  updateInquiryBackOffice(lineGuid: string, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/inquiry/' + 'backoffice-ep?coupon-line-guid=' + lineGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/inquiry/'
        + 'backoffice-ep?coupon-line-guid=' + lineGuid;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null,  this.getHttpHeader(visa));
  }

  cancelSNBackOffice(lineGuid: string, cancel: boolean, visa: ApiVisa):
    Observable<ApiResponseModel<CouponContainerModel>> {
    let url = this.apiUrl + '/serial-numbers/cancellation/backoffice-ep?coupon-line-guid=' + lineGuid + '&cancel=' + cancel;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/cancellation/backoffice-ep?coupon-line-guid='
        + lineGuid + '&cancel=' + cancel;
    }
    return this.httpClient.put<ApiResponseModel<CouponContainerModel>>
    (url, null,  this.getHttpHeader(visa));
  }

  uploadSNFileBackOffice(formData:FormData, hdrGuid: string, visa:ApiVisa){
    let url = this.apiUrl + '/serial-numbers/import/backoffice-ep/' + hdrGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path +'/serial-numbers/import/backoffice-ep/' + hdrGuid;
    }
    return this.httpClient.post(
      url,
      formData,
      this.getHttpHeader(visa)
    );
  }

  // ------------------Voucher Summary API---------------
  getVoucherSummary(hdrGuid: string, visa: ApiVisa): Observable<ApiResponseModel<VoucherSummaryResponseDto>> {
    let url = this.apiUrl + '/summary/' + hdrGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/summary/' + hdrGuid;
    }
    return this.httpClient.get<ApiResponseModel<VoucherSummaryResponseDto>>(
      url,
      this.getHttpHeader(visa)
    );
  }

  getSerialNumberInquiryHeaderPublic(voucherHdrCode: string, lineGuid: string, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/inquiry/public/' + voucherHdrCode + '?serial-number=' + lineGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/inquiry/public/' + voucherHdrCode + '?serial-number=' + lineGuid;
    }
    return this.httpClient.get<ApiResponseModel<serialNumberInquiryResponseDto>>(url, this.getHttpHeader(visa));
  }

  getSerialNumberInquiryPublic(lineGuid: string, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/inquiry/public?coupon-line-guid=' + lineGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/inquiry/public?coupon-line-guid=' + lineGuid;
    }
    return this.httpClient.get<ApiResponseModel<serialNumberInquiryResponseDto>>(url, this.getHttpHeader(visa));
  }

  redeemSerialNumber(couponLineGuid: string, entityGuid: string, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/redemption/login-entity-ep' + '?coupon-line-guid=' + couponLineGuid + '&entity-guid=' + entityGuid;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/redemption/login-entity-ep' + '?coupon-line-guid=' + couponLineGuid + '&entity-guid=' + entityGuid;
    }
    return this.httpClient.get<ApiResponseModel<CouponLineContainerModel>>(url, this.getHttpHeader(visa));
  }

  redeemSerialNumberBackOffice(guidList: string[], redeem: boolean, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/redemption/backoffice-ep' + '?redeem=' + redeem;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/redemption/backoffice-ep' + '?redeem=' + redeem;
    }
    return this.httpClient.put<ApiResponseModel<any>>(url, guidList, this.getHttpHeader(visa));
  }

  SerialNumberInquiryPublicWithAccessDetails(couponLineGuid: string, accessKeyId: string, accessKeySecret:string, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/inquiry/public/access' + '?coupon-line-guid=' + couponLineGuid
                + "&access-key-id=" + accessKeyId + "&access-key-secret=" + accessKeySecret;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/inquiry/public/access' + '?coupon-line-guid=' + couponLineGuid
      + "&access-key-id=" + accessKeyId + "&access-key-secret=" + accessKeySecret;
    }
    return this.httpClient.get<ApiResponseModel<serialNumberInquiryResponseDto>>(url, this.getHttpHeader(visa));
  }

  SerialNumberInquiryHeaderPublicWithAccessDetails(voucherHdrCode: string,lineGuid: string, accessKeyId: string, accessKeySecret:string, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/inquiry/public/' + voucherHdrCode+ '/access' + '?serial-number=' + lineGuid
                + "&access-key-id=" + accessKeyId + "&access-key-secret=" + accessKeySecret;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/inquiry/public/' + voucherHdrCode+ '/access' + '?serial-number=' + lineGuid
      + "&access-key-id=" + accessKeyId + "&access-key-secret=" + accessKeySecret;
    }
    return this.httpClient.get<ApiResponseModel<serialNumberInquiryResponseDto>>(url, this.getHttpHeader(visa));
  }

  SerialNumberRedemptionHeaderPublicWithAccessDetails(couponRedemptionRequest:CouponRedemptionRequestDtoModel, redeem:boolean, visa: ApiVisa) {
    let url = this.apiUrl + '/serial-numbers/redemption/public/access?redeem=' + redeem;
    if (this.endpoint_path && visa.api_domain_url) {
      url = visa.api_domain_url + this.endpoint_path + '/serial-numbers/redemption/public/access?redeem=' + redeem;
    }
    return this.httpClient.put<BasicApiResponseModel<boolean>>(url,couponRedemptionRequest, this.getHttpHeader(visa));
  }

  getCouponByEntityGuid(visa: ApiVisa) {
    const endpoint_path = Core2Config.TENANT_ECOM_URL_PREFIX + 'coupons/valid-by-login-subject/login-entity-ep';
    let url = Core2Config.DOMAIN_URL + endpoint_path;
    if (visa.api_domain_url) {
      url = visa.api_domain_url + endpoint_path;
    }
    return this.httpClient.get<ApiResponseModel<CouponContainerModel>>(url, this.getHttpHeader(visa));
  }

  getUsedCoupons(serverDocType: string, entityHdrGuid: string, visa: ApiVisa) {
    const endpoint_path = this.endpoint_path + '/used-coupons/login-entity-ep' + '?serverDocType=' + serverDocType
      + "&entityHdrGuid=" + entityHdrGuid;
    let url = Core2Config.DOMAIN_URL + endpoint_path;
    if (visa.api_domain_url) {
      url = visa.api_domain_url + endpoint_path;
    }
    return this.httpClient.get<ApiResponseModel<CouponContainerModel>>(url, this.getHttpHeader(visa));
  }

  getHdrByCriteriaWebsiteEp(
    pagination: Pagination,
    visa: ApiVisa,
    website_hdr_guid: string,
  ): Observable<PagingResponseModel<CouponContainerModel>> {
    if (this.endpoint_path && visa.api_domain_url) {
      this.apiUrl = visa.api_domain_url + this.endpoint_path;
    }
    const UrlByCriteria =
      this.apiUrl + '/headers/website-ep/query/' + website_hdr_guid + '?' + pagination.getQueryKeyValues();
    return this.httpClient.get<PagingResponseModel<CouponContainerModel>>(
      UrlByCriteria,
      this.getHttpHeader(visa)
    );
  }
}
